import { Box, Skeleton, Text, Touchable } from '@factorialco/deprecated-design-system'
import { PerformanceReviewEmployeeScoreGrid } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import WithToolTip from '../WithTooltip'
import { AvatarStack } from './AvatarStack'
import { disabledAvatarStack } from './index.vanilla.css'

const COMMON_GRID_PROPS = {
  padding: 's16',
  style: { width: '33%' },
  height: 's140',
  gridColSpan: { xxs: 4 },
  border: { all: { color: 'grey400', width: 's1', style: 'dashed' } },
  justifyContent: 'spaceBetween',
} as const

const CATEGORY_TO_STYLES = {
  '1': {
    background: 'radicalRed200',
    borderRadius: { bottomLeft: 'abs006' },
  },
  '2': {
    background: 'radicalRed100',
    borderRadius: undefined,
  },
  '3': {
    background: 'viridian100',
    borderRadius: { bottomRight: 'abs006' },
  },
  '4': {
    background: 'radicalRed100',
    borderRadius: undefined,
  },
  '5': {
    background: 'viridian100',
    borderRadius: undefined,
  },
  '6': {
    background: 'viridian200',
    borderRadius: undefined,
  },
  '7': {
    background: 'viridian100',
    borderRadius: { topLeft: 'abs006' },
  },
  '8': {
    background: 'viridian200',
    borderRadius: undefined,
  },
  '9': {
    background: 'viridian400',
    borderRadius: { topRight: 'abs006' },
  },
} as const

const getLabelByPosition = (position: NineBoxGridPosition) =>
  ({
    '1': i18n.t('performance_process.analytics.nine_box_grid.scores.1'),
    '2': i18n.t('performance_process.analytics.nine_box_grid.scores.2'),
    '3': i18n.t('performance_process.analytics.nine_box_grid.scores.3'),
    '4': i18n.t('performance_process.analytics.nine_box_grid.scores.4'),
    '5': i18n.t('performance_process.analytics.nine_box_grid.scores.5'),
    '6': i18n.t('performance_process.analytics.nine_box_grid.scores.6'),
    '7': i18n.t('performance_process.analytics.nine_box_grid.scores.7'),
    '8': i18n.t('performance_process.analytics.nine_box_grid.scores.8'),
    '9': i18n.t('performance_process.analytics.nine_box_grid.scores.9'),
  }[position])

export type NineBoxGridPosition = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9'

interface GridItemProps {
  position: NineBoxGridPosition
  scoreGrid?: PerformanceReviewEmployeeScoreGrid
  isEmpty: boolean
  totalEmployees: number
  onClick: () => void
  isActive: boolean
  showPercentage: boolean
}

export function GridItem({
  position,
  isEmpty,
  scoreGrid,
  totalEmployees,
  onClick,
  isActive,
  showPercentage,
}: GridItemProps) {
  const textColor = isEmpty ? 'grey600' : undefined

  const employeesCount = scoreGrid?.totalCount || 0
  const employeesPercentage = Math.round((employeesCount / (totalEmployees || 1)) * 100)
  const label = getLabelByPosition(position)
  const styles = CATEGORY_TO_STYLES[position]
  const tooltip = [label, showPercentage ? `(${employeesPercentage} %)` : undefined].join(' ')

  return (
    <Box
      borderRadius={styles.borderRadius}
      background={isEmpty ? undefined : isActive ? styles.background : 'grey200'}
      {...COMMON_GRID_PROPS}
      aria-label={i18n.t('performance_process.analytics.nine_box_grid.chart.filter_by', {
        label,
      })}
      onClick={onClick}
    >
      <Box>
        <Touchable
          onClick={(event) => {
            // We just want this for a11y purposes, since there needs to be a focusable element
            // but the event is handled by the parent Box, so mouse users can click the whole box
            event.preventDefault()
          }}
        >
          <WithToolTip label={tooltip} trackingGroup="nine-box-grid">
            <Text.Normal color={textColor} ellipsis size="200" weight="semibold">
              {label}
            </Text.Normal>
          </WithToolTip>
        </Touchable>
        <Box className={isActive ? '' : disabledAvatarStack}>
          <AvatarStack
            accesses={scoreGrid?.targetAccessConnection.nodes || []}
            totalCount={employeesCount}
          />
        </Box>
      </Box>
      <Box flexDirection="row" gap="s4">
        <Text.Normal color={textColor} size="200">
          {employeesCount}
        </Text.Normal>
        {showPercentage && (
          <Text.Normal color={textColor} size="200">
            ({i18n.formatNumber(employeesPercentage, 'decimal')}%)
          </Text.Normal>
        )}
      </Box>
    </Box>
  )
}

GridItem.Skeleton = function GridItemSkeleton({ position }: { position: NineBoxGridPosition }) {
  const label = getLabelByPosition(position)
  const styles = CATEGORY_TO_STYLES[position]

  return (
    <Box
      borderRadius={styles.borderRadius}
      {...COMMON_GRID_PROPS}
      aria-label={i18n.t('performance_process.analytics.nine_box_grid.chart.filter_by', {
        label,
      })}
    >
      <Skeleton width="s120" height="s20" />
      <Box flexDirection="row" gap="s4">
        <Skeleton width="s20" height="s20" />
        <Skeleton width="s20" height="s20" />
      </Box>
    </Box>
  )
}
